import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { loadModules } from 'esri-loader';
import { useAuth } from '../../../../AuthProvider';
import { addUnavailableNotice, applyDiscernShading, removeRetiredLayers } from '../../../../utils/discernWebmapShading';
import NationalAverageCrimeIndex from '../../../CustomVestmapViewer/components/NationalAverageCrimeIndex';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';
import data from '../../../CustomVestmapViewer/assets/data.json';

function CrimeMap({ webmapID, token, addressInfo }) {
  const { getAccessToken } = useAuth();
  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!webmapID || !token || !addressInfo) return;

    let view;
    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/WebMap',
      'esri/geometry/Extent',
      'esri/Graphic',
      'esri/widgets/Legend',
      'esri/config',
      'esri/layers/FeatureLayer',
    ]).then(
      ([Map, MapView, WebMap, Extent, Graphic, Legend, esriConfig, FeatureLayer]) => {
        if (!webmapID || !token) return;

        esriConfig.apiKey = token;

        const extent = new Extent({
          xmin: addressInfo.address_xmin,
          ymin: addressInfo.address_ymin,
          xmax: addressInfo.address_xmax,
          ymax: addressInfo.address_ymax,
          spatialReference: {
            wkid: addressInfo.spatialReference?.wkid || 4326,
          },
        });

        const webmap = new WebMap({
          portalItem: {
            id: webmapID,
          },
        });

        // Redraw the shaded layer from 2026 GeoEnrichment values (flagged; any
        // failure or signed-out view keeps the authored 2024 layer).
        webmap.when(() => {
          removeRetiredLayers(webmap);
          applyDiscernShading({
            webmap,
            addressInfo,
            getAccessToken,
            deps: { FeatureLayer, Graphic },
            isStale: () => view?.destroyed === true || webmap?.destroyed === true,
            onUnavailable: () => addUnavailableNotice(view),
          });
        });

        view = new MapView({
          container: mapRef.current,
          map: webmap,
          extent: extent,
          zoom: 14,
          navigation: {
            mouseWheelZoomEnabled: false,
          },
        });

        viewRef.current = view;

        view
          .when(() => {
            setIsMapLoaded(true);
            setErrorMessage(null);

            view.ui.add(
              new Legend({
                view: view,
              }),
              'top-right',
            );

            if (
              addressInfo.address_longitude &&
              addressInfo.address_latitude
            ) {
              const customSymbol = {
                type: 'picture-marker',
                url: marker,
                width: '50px',
                height: '100px',
              };

              view.graphics.add(
                new Graphic({
                  geometry: {
                    type: 'point',
                    longitude: addressInfo.address_longitude,
                    latitude: addressInfo.address_latitude,
                  },
                  symbol: customSymbol,
                }),
              );
            }
          })
          .catch(err => {
            console.error('Error loading crime map:', err);
            setIsMapLoaded(false);
            setErrorMessage(err.message || 'Failed to load map');
          });
      },
    );

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, [webmapID, token, addressInfo]);

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          sm: '30rem',
          xs: '20rem',
        },
        position: 'relative',
      }}
    >
      {!isMapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {errorMessage ? (
            <Typography
              variant='body2'
              sx={{ color: '#b00020', textAlign: 'center', p: 2 }}
            >
              {errorMessage}
            </Typography>
          ) : (
            <CircularProgress sx={{ color: '#4FA490' }} />
          )}
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      />
    </Box>
  );
}

const CrimeCard = ({ type, addressInfo, crimeData, webmapID, arcgisToken }) => {
  if (type === 'map') {
    if (!webmapID || !arcgisToken || !addressInfo) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view crime map
          </Typography>
        </Box>
      );
    }

    return <CrimeMap webmapID={webmapID} token={arcgisToken} addressInfo={addressInfo} />;
  }

  if (type === 'data') {
    if (!crimeData || Object.keys(crimeData).length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view crime data
          </Typography>
        </Box>
      );
    }

    const blocks = [];

    const crimeFieldTitles = {
      overall_crime_index_score: 'Overall Crime Index Score',
      property_crime_index_score: 'Property Crime Index Score',
      personal_crime_index_score: 'Personal Crime Index Score',
      murder_index_score: 'Murder Index Score',
      sexual_assault_index_score: 'Sexual Assault Index Score',
      robbery_index_score: 'Robbery Index Score',
      assault_index_score: 'Assault Index Score',
      burglary_index_score: 'Burglary Index Score',
      larceny_index_score: 'Larceny Index Score',
      motor_vehicle_theft_index_score: 'Motor Vehicle Theft Index Score',
      total_crime_index: 'Total Crime Index',
      property_crime_index: 'Property Crime Index',
      personal_crime_index: 'Personal Crime Index',
      murder_index: 'Murder Index',
      sexual_assault_index: 'Sexual Assault Index',
      robbery_index: 'Robbery Index',
      assault_index: 'Assault Index',
      burglary_index: 'Burglary Index',
      larceny_index: 'Larceny Index',
      motor_vehicle_theft_index: 'Motor Vehicle Theft Index',
      CRMCYTOTC: 'Total Crime Index',
      CRMCYPROC: 'Property Crime Index',
      CRMCYPERC: 'Personal Crime Index',
      CRMCYMURD: 'Murder Index',
      CRMCYRAPE: 'Sexual Assault Index',
      CRMCYROBB: 'Robbery Index',
      CRMCYASST: 'Assault Index',
      CRMCYBURG: 'Burglary Index',
      CRMCYLARC: 'Larceny Index',
      CRMCYMVEH: 'Motor Vehicle Theft Index',
    };

    Object.keys(crimeData).forEach(fieldKey => {
      const value = crimeData[fieldKey];
      if (value !== undefined && value !== null && value !== '') {
        let title = crimeFieldTitles[fieldKey];
        
        if (!title && data[fieldKey] && data[fieldKey].title) {
          title = data[fieldKey].title;
        }
        
        if (!title) {
          title = fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
        
        blocks.push(
          <NationalAverageCrimeIndex
            key={fieldKey}
            sectionBlock={{
              title: title,
              group: title,
              value: { [fieldKey]: value },
              outfields: [fieldKey],
              key: 'crime',
            }}
            hideTooltips
          />,
        );
      }
    });

    if (blocks.length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            No crime data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: '1rem',
        }}
      >
        {blocks.map(block => block)}
      </Box>
    );
  }

  return null;
};

export default CrimeCard;

